import { useCallback, useEffect, useState } from 'react';
import { Button, Flex, useBreakpointValue } from '@chakra-ui/react';

import { useAuth } from '../../../hooks/auth';
import DataTableSearch from './DataTableSearch';
import DataTableTable from './DataTableTable';
import DataTableCards from './DataTableCards';
import DataTablePaginate from './DataTablePaginate';
import ModalApontamento from '../ModalApontamento';

type ControlsData = {
	page: number;
	perPage: number;
	totalPage: number;
}
type FichaData = {
	banca: string;
	cliente: string;
	cod_emp: number;
	cod_ficha: number;
	cod_ped: string;
	cod_pro: number;
	cod_event: number;
	dat_cad: string;
	descricao: string;
	imagem: string;
	nome_pro: string;
	proxima_op: string;
	quant_atual:number;
	quant_incial: number;
	status_des: string;
	usuario: string;
	apontamento: boolean;
}
interface DataTableFichasParams {
	data: FichaData[];
	setModalApontaScroll: (open: boolean) => void;
	handleGetFichas: (data: any) => void;
	dataGetFichas: any;
}

const DataTableFichas: React.FC<DataTableFichasParams> = ({ 
	data,
	setModalApontaScroll,
	handleGetFichas,
	dataGetFichas
}) => {
	const { permissions } = useAuth();

  const isWideVersion = useBreakpointValue({
    base: false,
    lg: true,
  });

  const [loading, setLoading] = useState(true);

	const [pesqFichas, setPesqFichas] = useState<FichaData[]>(data);
	const [fichas, setFichas] = useState<FichaData[]>(data);

  const [sort, setSort] = useState({ por: '', cres: true });
	const [controls, setControls] = useState<ControlsData>({
		page: 1,
		perPage: 10,
		totalPage: Math.ceil(data.length / 10),
	});

	const [modalApontaVisible, setModalApontaVisible] = useState(false);
	const [apontas, setApontas] = useState<FichaData[]>([]);

  useEffect(() => {
    setFichas(data.slice(0, 10));
    setLoading(false);
  }, []);

  const handleUpdateList = useCallback((
    page: number,
    perPage: number,
		fichass: FichaData[],
  ) => {
    const auxPage = page - 1;
    const start = auxPage * perPage;
    const end = start + perPage;

		setFichas(fichass.slice(start, end));
  }, []);

	const handleAddAponta = useCallback((cod_ficha: number) => {
		const dataExists = data.findIndex(a => a.cod_ficha === cod_ficha);
		
		if (dataExists < 0) {
			return;
		};

		const auxFicha = { ...data[dataExists], apontamento: true };
		data[dataExists] = auxFicha;

		const apontasExists = apontas.findIndex(a => a.cod_ficha === cod_ficha);
		if (apontasExists >= 0) {
			return;
		};

		const auxPesqs = pesqFichas;
		const indexPesq = auxPesqs.findIndex(a => a.cod_ficha === cod_ficha);
		auxPesqs[indexPesq] = { ...auxPesqs[indexPesq], apontamento: true };
		setPesqFichas([...auxPesqs]);

		const auxFichas = fichas;
		const indexFicha = auxFichas.findIndex(a => a.cod_ficha === cod_ficha);
		auxFichas[indexFicha] = auxFicha;
		setFichas([...auxFichas]);

		const auxApontas = apontas;
		auxApontas.push(auxFicha);
		setApontas([...auxApontas]);

		handleUpdateList(controls.page, controls.perPage, auxPesqs);
	}, [controls, pesqFichas, fichas, apontas]);

	const handleRemoveAponta = useCallback((cod_ficha: number) => {
		const dataExists = data.findIndex(a => a.cod_ficha === cod_ficha);
		data[dataExists] = { ...data[dataExists], apontamento: false };

		const auxApontas = apontas;
		const indexAponta = apontas.findIndex(a => a.cod_ficha === cod_ficha);
		auxApontas.splice(indexAponta, 1);
		setApontas([...auxApontas]);
		if (auxApontas.length <= 0) {
			setModalApontaVisible(false);
		}

		const auxFichas = fichas;
		const indexFicha = fichas.findIndex(a => a.cod_ficha === cod_ficha);
		auxFichas[indexFicha] = { ...auxFichas[indexFicha], apontamento: false };
		setFichas([...auxFichas]);

		const auxPesqs = pesqFichas;
		const indexPesq = auxPesqs.findIndex(a => a.cod_ficha === cod_ficha);

		auxPesqs[indexPesq] = { ...auxPesqs[indexPesq], apontamento: false };
		setPesqFichas([...auxPesqs]);

		handleUpdateList(controls.page, controls.perPage, auxPesqs);
	}, [controls, pesqFichas, fichas, apontas]);

	const handleCleanAponta = useCallback(() => {
		const auxFichas = fichas;
		const auxPesqs = pesqFichas;

		for (let i = 0; i < apontas.length; i++) {
			const dataExists = data.findIndex(a => a.cod_ficha === apontas[i].cod_ficha);
			data[dataExists] = { ...data[dataExists], apontamento: false };

			const indexFicha = fichas.findIndex(a => a.cod_ficha === apontas[i].cod_ficha);
			auxFichas[indexFicha] = { ...auxFichas[indexFicha], apontamento: false };

			const indexPesq = auxPesqs.findIndex(a => a.cod_ficha === apontas[i].cod_ficha);
			auxPesqs[indexPesq] = { ...auxPesqs[indexPesq], apontamento: false };
		};
	
		setFichas([...auxFichas]);
		setPesqFichas([...auxPesqs]);
		handleUpdateList(controls.page, controls.perPage, auxPesqs);
		setApontas([]);
	}, [controls, pesqFichas, fichas, apontas]);

  return (
		<>
			{modalApontaVisible && (
				<ModalApontamento
					isVisible={modalApontaVisible}
					onClose={() => setModalApontaVisible(false)}
					apontamentos={apontas}
					handleAddAponta={handleAddAponta}
					handleRemoveAponta={handleRemoveAponta}
					setModalApontaScroll={setModalApontaScroll}
					handleCleanAponta={handleCleanAponta}
					handleGetFichas={handleGetFichas}
					dataGetFichas={dataGetFichas}
				/>
			)}

			<Flex w="100%" flexDir="column">
				<Flex w="100%">
					<DataTableSearch
						fichas={data}
						setPesqFichas={setPesqFichas}
						controls={controls}
						setControls={setControls}
						handleUpdateList={handleUpdateList}
					/>

					{/* <Button
						colorScheme="red"
						bg="#c53030"
						mr={6}
						disabled={apontas.length === 0}
						onClick={handleCleanAponta}
					>
						Limpar
					</Button> */}

					{permissions.alterar && (
						<Button
							colorScheme="blue"
							fontSize={[11, 16]}
							mr={[0, 0, 0, 6]}
							// disabled={apontas.length === 0}
							onClick={() => {
								setModalApontaVisible(true);
								setModalApontaScroll(true);
							}}
						>
							Apontamento
						</Button>
					)}
				</Flex>

				{isWideVersion && (
					<DataTableTable
						fichas={fichas}
						setFichas={setFichas}
						pesqFichas={pesqFichas}
						setPesqFichas={setPesqFichas}
						sort={sort}
						setSort={setSort}
						controls={controls}
						handleUpdateList={handleUpdateList}
						aponta={apontas}
						setAponta={setApontas}
					/>
				)}

				{!isWideVersion && !loading && (
					<DataTableCards
						fichas={fichas}
						setFichas={setFichas}
						pesqFichas={pesqFichas}
						setPesqFichas={setPesqFichas}
						controls={controls}
						handleUpdateList={handleUpdateList}
						aponta={apontas}
						setAponta={setApontas}
					/>
				)}

				<DataTablePaginate
					pesq_length={pesqFichas.length}
					pesqFichas={pesqFichas}
					controls={controls}
					setControls={setControls}
					handleUpdateList={handleUpdateList}
				/>
			</Flex>
		</>
  );
};

export default DataTableFichas;
